import API_URL from '../config';
import { getAuthHeaders } from '../utils/api';

/**
 * Create a new puzzle
 * @param {Object} puzzleData - { imageData, title?, rows?, columns? }
 * @returns {Promise<Object>} Created puzzle
 */
export const createPuzzle = async (puzzleData) => {
  const response = await fetch(`${API_URL}/api/puzzles`, {
    method: 'POST',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(puzzleData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create puzzle');
  }

  return response.json();
};

/**
 * Get a single puzzle by ID
 * @param {string} puzzleId - Puzzle ID
 * @returns {Promise<Object>} Puzzle data
 */
export const getPuzzle = async (puzzleId) => {
  const response = await fetch(`${API_URL}/api/puzzles/${puzzleId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch puzzle');
  }

  return response.json();
};

/**
 * Get all puzzles for authenticated user
 * @returns {Promise<Array>} List of puzzles
 */
export const getUserPuzzles = async () => {
  const response = await fetch(`${API_URL}/api/puzzles`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch puzzles');
  }

  return response.json();
};

/**
 * Save puzzle progress
 * @param {string} puzzleId - Puzzle ID
 * @param {Object} stateData - { pieces?, isComplete?, playTime?, moves? }
 * @returns {Promise<Object>} Update result
 */
export const savePuzzleState = async (puzzleId, stateData) => {
  const response = await fetch(`${API_URL}/api/puzzles/${puzzleId}/state`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(stateData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save puzzle state');
  }

  return response.json();
};

/**
 * Delete a puzzle
 * @param {string} puzzleId - Puzzle ID
 * @returns {Promise<Object>} Delete result
 */
export const deletePuzzle = async (puzzleId) => {
  const response = await fetch(`${API_URL}/api/puzzles/${puzzleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete puzzle');
  }

  return response.json();
};

/**
 * Claim an anonymous puzzle for the authenticated user
 * @param {string} puzzleId - Puzzle ID
 * @returns {Promise<Object>} Claim result
 */
export const claimPuzzle = async (puzzleId) => {
  const response = await fetch(`${API_URL}/api/puzzles/${puzzleId}/claim`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to claim puzzle');
  }

  return response.json();
};
